/**
 * The colour a parcel gets from its Bodenrichtwert.
 *
 * WHY A LOGARITHMIC SCALE
 * -----------------------
 * The values span four orders of magnitude within a single city. Berlin's 2026 vintage runs from
 * 0.6 €/m² on forest to 60,000 €/m² at Potsdamer Platz, with a median of 480. On a linear ramp
 * everything below a thousand euros is the same colour — which is to say, everything residential,
 * which is what the reader is looking at. Log10 puts the median in the middle of the ramp and keeps
 * the difference between 300 and 600 visible, which is the difference between two neighbourhoods.
 *
 * WHY FIXED ANCHORS AND NOT THE VIEW'S OWN RANGE
 * ----------------------------------------------
 * A scale computed from the parcels currently on screen recolours itself on every pan, and two
 * screenshots of neighbouring districts then use the same red for different values. The anchors
 * below are national: they hold for a Berlin block and for a field in the Altmark, and a colour
 * means the same thing wherever the map is pointed.
 */

/** Bottom and top of the ramp, in €/m². Below and above, the end colours simply hold. */
const MIN_EUR = 1;
const MAX_EUR = 10000;

/**
 * Green (cheap) to red (expensive), the ramp the map was asked for.
 *
 * WHY THE LIGHTNESS FALLS MONOTONICALLY
 * -------------------------------------
 * A green-to-red ramp is the one everybody reads without a key, but it is also the one pair of
 * hues that red-green colour blindness collapses — roughly 8 % of men see the two ends as much
 * the same brownish tone. The usual textbook ramp (RdYlGn) makes that worse by putting its
 * lightest colour in the *middle*: cheap and expensive then look alike, and mid-priced stands out.
 *
 * These six steps therefore get steadily darker from one end to the other. Whoever cannot tell
 * the hues apart still reads the order, because pale means cheap and dark means expensive all the
 * way along. It costs nothing for everyone else.
 *
 * (For the record, this replaces a deliberately neutral viridis ramp. The argument for that one
 * was that high land value is not "good" — a cheap plot is exactly what a developer is looking
 * for, and green/red reads as a verdict. That was a preference, not a fact, and it was overruled.)
 */
const RAMP = [
    '#a8dda0', '#5fae5a', '#d9c93c', '#e8912f', '#c9452a', '#7d1518',
];

/**
 * What the Nutzungsart shorthand means, in words.
 *
 * The register speaks in codes — `W`, `MD`, `GE`, `SE` — and a tooltip line reading just "W" is
 * a line that costs the reader more than it gives. These are the shorthands of Anlage 5 to the
 * ImmoWertV, the same list every Gutachterausschuss draws on, taken from the AdV code list that
 * the source data itself links to.
 *
 * Kept as a plain map rather than looked up at runtime: it is a closed, published vocabulary that
 * changes with the ordinance, not with the data, and the map has to render a tooltip per mouse
 * move.
 */
const NUTA_LABEL: Record<string, string> = {
    // Art der Nutzung — Art der baulichen oder sonstigen Nutzung gemäß Anlage 5 zur ImmoWertV
    A: 'Acker',
    AB: 'Abbauland',
    CA: 'Campingplatz',
    EGA: 'Erwerbsgartenbaufläche',
    F: 'forstwirtschaftliche Fläche',
    FGA: 'Freizeitgartenfläche',
    FH: 'Friedhof',
    FP: 'Flughafen, Flugplätze usw.',
    G: 'gewerbliche Baufläche',
    GB: 'Baufläche für Gemeinbedarf',
    GE: 'Gewerbegebiet',
    GF: 'Gemeinbedarfsfläche (kein Bauland)',
    GI: 'Industriegebiet',
    GR: 'Grünland',
    KGA: 'Kleingartenfläche (Bundeskleingartengesetz)',
    KUP: 'Kurzumtriebsplantagen, Agroforst',
    L: 'landwirtschaftliche Fläche',
    LG: 'Lagerfläche',
    M: 'gemischte Baufläche',
    MD: 'Dorfgebiet',
    MDW: 'Dörfliches Wohngebiet',
    MI: 'Mischgebiet',
    MK: 'Kerngebiet',
    MU: 'Urbanes Gebiet',
    PG: 'private Grünfläche',
    PP: 'private Parkplätze, Stellplatzfläche',
    S: 'Sonderbaufläche',
    SE: 'Sondergebiet für Erholung',
    SG: 'sonstige private Fläche',
    SK: 'Anbaufläche für Sonderkulturen',
    SN: 'Sondernutzungsfläche',
    SO: 'sonstige Sondergebiete',
    SPO: 'Sportfläche (u.a. Golfplatz)',
    UN: 'Unland, Geringstland, Bergweide, Moor',
    W: 'Wohnbaufläche',
    WA: 'allgemeines Wohngebiet',
    WB: 'besonderes Wohngebiet',
    WF: 'Wasserfläche',
    WG: 'Weingarten',
    WR: 'reines Wohngebiet',
    WS: 'Kleinsiedlungsgebiet',
    // Ergänzung — Ergänzung zur Art der Nutzung gemäß Anlage 5 zur ImmoWertV
    ASB: 'Bebaute Flächen im Außenbereich',
    BGH: 'Büro- und Geschäftshäuser',
    BH: 'Bürohäuser',
    BI: 'Bildungseinrichtungen',
    BLU: 'Blumen- und Zierpflanzenanbaufläche',
    BMS: 'Baumschulfläche',
    EE: 'Bauflächen für Energieerzeugung',
    EFH: 'Ein- und Zweifamilienhäuser',
    EKZ: 'Einkaufszentren, großflächiger Einzelhandel',
    FEH: 'Ferienhäuser',
    FL: 'Weingarten in Flachlage',
    FZT: 'Freizeit und Touristik',
    GAR: 'Garagen, Stellplatzanlagen, Parkhäuser',
    GD: 'Handel und dienstleistungsorientiertes Gewerbe',
    GEM: 'Gemüseanbaufläche',
    GH: 'Geschäftshäuser (mehrgeschossig)',
    HAF: 'Hafen',
    HL: 'Weingarten in Hanglage',
    HPF: 'Hopfenanbaufläche',
    KOH: 'Braunkohletagebau',
    LAD: 'Läden (eingeschossig), nicht großflächiger Einzelhandel',
    LP: 'landwirtschaftliche Produktion',
    MED: 'Gesundheitseinrichtungen',
    MES: 'Messen, Ausstellungen, Kongresse, Großveranstaltungen aller Art',
    MFH: 'Mehrfamilienhäuser',
    MIL: 'Militär',
    OG: 'Obstanbaufläche',
    PL: 'Produktion und Logistik',
    SND: 'Abbauland von Sand und Kies',
    SOW: 'Sozialer Mietwohnungsbau',
    SPA: 'Spargelanbaufläche',
    STL: 'Weingarten in Steillage',
    STN: 'Steinbruch',
    TAB: 'Tabakanbaufläche',
    TOF: 'Abbauland von Torf',
    TON: 'Abbauland von Ton und Mergel',
    WGH: 'Wohn- und Geschäftshäuser',
    WO: 'Wochenendhäuser',
};

/**
 * A Nutzungsart as it should appear on screen.
 *
 * Three states deliver the bare shorthand (`W`), Berlin delivers its own long form already
 * spelled out (`W - Wohngebiet`, `SF-KGA - Sonstige Flächen - Kleingartenfläche`). Anything that
 * already carries a space is therefore passed through untouched — expanding it a second time
 * would produce "Wohnbaufläche (W - Wohngebiet)".
 *
 * An unknown code is returned as it stands. Hesse writes `LW`, which is not in the ordinance's
 * list; showing it raw is honest, dropping it would lose the only statement the source made.
 */
export const nutaLabel = (nuta?: string | null): string | null => {
    if (!nuta) return null;
    const code = String(nuta).trim();
    if (/\s/.test(code)) return code;
    const label = NUTA_LABEL[code];
    // The code stays visible next to the words: whoever knows the register reads it faster, and
    // whoever does not now has both halves of the translation in front of them.
    return label ? `${label} (${code})` : code;
};

/** The parcel has zones but no single value — Berlin's GFZ ladders, Hesse's coincident zones. */
export const AMBIGUOUS_COLOR = '#9e9e9e';

/** No Bodenrichtwert at all: the old faint outline, so the map still reads underneath. */
export const NEUTRAL_COLOR = '#C8A24A';

export interface ParcelBrw {
    wert?: number | null;
    auswahl?: 'einzig' | 'flaechengewichtet' | 'nachGfz' | 'mehrdeutig' | null;
    spanne?: { min: number; max: number; n: number } | null;
    stag?: string | null;
    nuta?: string | null;
    /**
     * Only ever a GFZ or a WGFZ — never any other density measure.
     *
     * Brandenburg publishes four kinds side by side, and in nine of ten populated cases
     * it is the *number of full storeys*, not a ratio. A `2` shown as "GFZ 2" would not
     * be imprecise, it would be a different quantity: a GFZ of 2 means twice the plot
     * area in floor space, which is four to six storeys at usual coverage. The pipeline
     * therefore leaves this field empty unless the kind really is GFZ/WGFZ, and puts
     * everything else in `dichte` below, where the kind travels with the number.
     */
    gfz?: number | null;
    /** The density as published, with its kind. `art` is one of GFZ, WGFZ, ZVG, GRZ. */
    dichte?: { wert?: number | null; art?: string | null } | null;
    /**
     * The source itself recorded the overlap — Hesse writes "Zone ist deckungsgleich mit
     * einer anderen Bodenrichtwertzone" in its remarks field. A statement of the
     * Gutachterausschuss, not a reading of ours.
     */
    coincident?: boolean | null;
    /**
     * The zones covering this plot differ in a property of the *plot* — waterfront,
     * building type, permitted density. Several values apply and which one does is
     * decided by the plot, not by the location. Weaker than `coincident`, which is the
     * source saying so itself.
     */
    differentiated?: boolean | null;
    uncoveredPct?: number | null;
    bodenwertEur?: number | null;
    zonen?: number | null;
}

/** Position on the ramp, 0..1. */
const position = (eur: number): number => {
    const clamped = Math.min(Math.max(eur, MIN_EUR), MAX_EUR);
    return (Math.log10(clamped) - Math.log10(MIN_EUR)) / (Math.log10(MAX_EUR) - Math.log10(MIN_EUR));
};

export const brwColor = (eur: number): string => {
    const p = position(eur);
    // Nearest stop rather than interpolation: six steps read as six classes, and a reader comparing
    // two parcels should see either "the same" or "different", not a gradient they have to measure.
    return RAMP[Math.min(RAMP.length - 1, Math.round(p * (RAMP.length - 1)))];
};

/** The legend the map shows beside the scale, as value/colour pairs. */
export const brwLegend = (): Array<{ label: string; color: string }> =>
    RAMP.map((color, index) => {
        const p = index / (RAMP.length - 1);
        const eur = Math.pow(10, Math.log10(MIN_EUR) + p * (Math.log10(MAX_EUR) - Math.log10(MIN_EUR)));
        return { label: `${Math.round(eur).toLocaleString('de-DE')} €/m²`, color };
    });

/**
 * How one parcel is painted.
 *
 * The ambiguous case is deliberately *not* given a value colour. Eleven percent of Berlin's parcels
 * carry more than one Bodenrichtwert — 450 € at a GFZ of 0.6 and 600 € at 1.0 for the same ground —
 * and those are two different quantities, not two estimates of one. Painting the mean would invent
 * a number the register does not contain; painting the higher one would flatter the map. Grey says
 * "several apply", and the tooltip says which.
 */
export const brwFill = (brw: ParcelBrw | null | undefined): { color: string; opacity: number } => {
    if (!brw) return { color: NEUTRAL_COLOR, opacity: 0.06 };
    if (brw.auswahl === 'mehrdeutig' || brw.wert == null) {
        return { color: AMBIGUOUS_COLOR, opacity: 0.45 };
    }
    return { color: brwColor(brw.wert), opacity: 0.55 };
};

/** The lines the tooltip adds when the map is in Bodenrichtwert mode. */
export const brwTooltipLines = (
    brw: ParcelBrw | null | undefined,
    format: (value: number) => string,
): string[] => {
    if (!brw) return [];
    const lines: string[] = [];

    if (brw.auswahl === 'mehrdeutig' && brw.spanne) {
        // The range, and why there is one. Without the reason a reader takes it for imprecision.
        const grund = brw.coincident
            ? ' · deckungsgleiche Zonen'
            : brw.differentiated
              ? // Naming the reason matters most here: in Brandenburg the second value is
                // usually the waterfront premium — Schwerin runs 200 €/m² and 800 €/m² for
                // the same ground. A bare range reads as uncertainty; it is not.
                ' · je nach Grundstücksmerkmal'
              : '';
        lines.push(
            `${format(brw.spanne.min)}–${format(brw.spanne.max)} €/m² · ${brw.spanne.n} Stufen${grund}`,
        );
    } else if (brw.wert != null) {
        const wie =
            brw.auswahl === 'flaechengewichtet'
                ? ` · flächengewichtet aus ${brw.zonen ?? 2} Zonen`
                : brw.auswahl === 'nachGfz'
                  ? ` · nach GFZ ${brw.gfz ?? '?'}`
                  : '';
        lines.push(`${format(brw.wert)} €/m²${wie}`);
    }

    if (brw.bodenwertEur != null) lines.push(`Bodenwert ${format(brw.bodenwertEur)} €`);
    // In words, not in register shorthand — a tooltip entry reading "W" is a lookup task.
    const nutzung = nutaLabel(brw.nuta);
    if (nutzung) lines.push(nutzung);

    // The density the value refers to, named by its kind. A Bodenrichtwert is quoted for
    // a reference plot; without the reference the number is only half a statement — 120
    // €/m² at two storeys and 120 €/m² at five are not the same offer.
    if (brw.dichte?.wert != null) {
        const zahl = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 }).format(
            brw.dichte.wert,
        );
        lines.push(
            brw.dichte.art === 'ZVG'
                ? `${zahl} Vollgeschosse`
                : `${brw.dichte.art ?? 'Dichte'} ${zahl}`,
        );
    }
    // The Stichtag is not decoration: Hesse stands at 2024-01-01 while Berlin and Saxony-Anhalt
    // stand at 2026-01-01, so any comparison across states mixes vintages. Berlin revalues yearly,
    // Hesse every two years — they will not line up again.
    if (brw.stag) lines.push(`Stichtag ${brw.stag}`);
    // Only worth saying when it is substantial: an edge parcel that sticks out of every zone.
    if (Number.isFinite(brw.uncoveredPct) && (brw.uncoveredPct as number) > 5) {
        lines.push(`${Math.round(brw.uncoveredPct as number)} % ohne Bodenrichtwert`);
    }
    return lines;
};
